import { makeModuleApiInfoSelector, makeModuleI18nSelector } from "@client/content/selectors";
import { apiAction } from "@client/lib/actions";
import { parseQueryString } from "@client/lib/api";
import { replaceUrlQuery } from "@client/lib/navigation";
import { TargetSelector, getSettingValueSelector } from "@client/selectors";
import { type EntityRef, type GenericMap, Status } from "@client/types";
import { type AppState, type BaseAppState } from "@client/types/AppState";
import { omit } from "lodash";
import { type Dispatch } from "redux";
import { initAction, redirect } from "../actions";
import api from "./api";
import { localStateSelector } from "./selectors";
import { type LoginForm } from "./types";

export enum Actions {
  InitBegin = "MODULE_LOGIN_FORM_INIT_BEGIN",
  InitFailure = "MODULE_LOGIN_FORM_INIT_FAILURE",
  InitSuccess = "MODULE_LOGIN_FORM_INIT_SUCCESS",

  LoginBegin = "MODULE_LOGIN_FORM_LOGIN_BEGIN",
  LoginFailure = "MODULE_LOGIN_FORM_LOGIN_FAILURE",
  LoginSuccess = "MODULE_LOGIN_FORM_LOGIN_SUCCESS",

  ResendLinkBegin = "MODULE_LOGIN_FORM_RESEND_LINK_BEGIN",
  ResendLinkFailure = "MODULE_LOGIN_FORM_RESEND_LINK_FAILURE",
  ResendLinkSuccess = "MODULE_LOGIN_FORM_RESEND_LINK_SUCCESS",

  ResetPasswordBegin = "MODULE_LOGIN_FORM_RESET_PASSWORD_BEGIN",
  ResetPasswordFailure = "MODULE_LOGIN_FORM_RESET_PASSWORD_FAILURE",
  ResetPasswordSuccess = "MODULE_LOGIN_FORM_RESET_PASSWORD_SUCCESS",

  SendResetLinkBegin = "MODULE_LOGIN_FORM_SEND_RESET_LINK_BEGIN",
  SendResetLinkFailure = "MODULE_LOGIN_FORM_SEND_RESET_LINK_FAILURE",
  SendResetLinkSuccess = "MODULE_LOGIN_FORM_SEND_RESET_LINK_SUCCESS",

  SetFieldErrors = "MODULE_LOGIN_SET_FIELD_ERRORS",
  SetMode = "MODULE_LOGIN_SET_MODE"
}

export type InitBeginActionData = EntityRef & {};
export type InitSuccessActionData = LoginForm.Api.InitResponse;
const _init = initAction<AppState, InitBeginActionData, LoginForm.Api.InitResponse, InitSuccessActionData>({
  types: {
    begin: Actions.InitBegin,
    failure: Actions.InitFailure,
    success: Actions.InitSuccess
  },
  action: (state: BaseAppState, dispatch: any, args: InitBeginActionData) => {
    const apiInfo = makeModuleApiInfoSelector()(state, args);
    const qs = parseQueryString(window.location.href) || {};

    return api.init(apiInfo, { t: qs.t, s: qs.s });
  }
});

export type InitArgs = EntityRef & {};
export const init = (args: InitArgs) => (dispatch: Dispatch<any>, getState: () => AppState) => {
  const state = getState();
  const localState = localStateSelector(state, args);
  if (localState.status === Status.None) {
    dispatch(_init(args));
  }
};

export type LoginBeginActionData = EntityRef & {
  email: string;
  password: string;
  redirectUrl?: string;
};
export type LoginSuccessActionData = LoginForm.Api.LoginResponse;
const _login = apiAction<AppState, LoginBeginActionData, LoginForm.Api.LoginResponse, LoginSuccessActionData>({
  types: {
    begin: Actions.LoginBegin,
    failure: Actions.LoginFailure,
    success: [
      ({ dispatch, res, params, getState }) => {
        if (res.isAdmin) return;

        if (res.user) {
          const postscript = getSettingValueSelector(TargetSelector.Entity, "postscript")(getState(), params);
          if (postscript) {
            try {
              eval(postscript.replace("{USER}", JSON.stringify(res.user)));
            } catch {}
          }
        }

        return dispatch(
          redirect({
            fallbackUrl: res.homeUrl,
            id: params.id,
            reload: true,
            source: params.source,
            target: TargetSelector.Entity
          })
        );
      },
      Actions.LoginSuccess
    ]
  },
  action: (state: AppState, dispatch: any, args: LoginBeginActionData) => {
    const apiInfo = makeModuleApiInfoSelector()(state, args);
    return api.login(apiInfo, {
      email: args.email,
      password: args.password,
      qs: window.location.search
    });
  }
});

export const login = (ref: EntityRef, data: LoginForm.View.LoginFormData) => (dispatch: Dispatch<any>) => {
  const { password, email } = data;
  dispatch(
    _login({
      ...ref,
      email: email,
      password
    })
  );
};

export type ResetPasswordBeginActionData = EntityRef & {
  password: string;
  repeatPassword: string;
};
export type ResetPasswordSuccessActionData = LoginForm.Api.ResetPasswordResponse;
const _resetPassword = apiAction<
  BaseAppState,
  ResetPasswordBeginActionData,
  LoginForm.Api.ResetPasswordResponse,
  ResetPasswordSuccessActionData
>({
  types: {
    begin: Actions.ResetPasswordBegin,
    failure: Actions.ResetPasswordFailure,
    success: [Actions.ResetPasswordSuccess, ({ dispatch, params }) => dispatch(setMode(params, "login"))]
  },
  action: (state: BaseAppState, dispatch: any, args: ResetPasswordBeginActionData) => {
    const apiInfo = makeModuleApiInfoSelector()(state, args);

    const qs = parseQueryString(window.location.href) || {};
    return api.resetPassword(apiInfo, {
      password: args.password,
      repeatPassword: args.repeatPassword,
      t: qs.t,
      s: qs.s
    });
  }
});

export const resetPassword =
  (ref: EntityRef, data: LoginForm.View.ResetPasswordFormData) =>
  (dispatch: Dispatch<any>, getState: () => BaseAppState) => {
    const state = getState();
    const i18n = makeModuleI18nSelector()(state, ref);
    const localState = localStateSelector(state, ref);

    if (localState.status !== Status.Saving) {
      if (data.password !== data.repeatPassword) {
        dispatch(
          setFieldErrors(ref, {
            repeatPassword: i18n.passwordsDontMatch
          })
        );
      } else {
        dispatch(_resetPassword({ ...ref, ...data }));
      }
    }
  };

export type SendResetLinkBeginActionData = EntityRef & {
  email: string;
};
export type SendResetLinkSuccessActionData = void;
const _sendResetLink = apiAction<BaseAppState, SendResetLinkBeginActionData, void, ResetPasswordSuccessActionData>({
  types: {
    begin: Actions.SendResetLinkBegin,
    failure: Actions.SendResetLinkFailure,
    success: Actions.SendResetLinkSuccess
  },
  action: (state: BaseAppState, dispatch: any, args: SendResetLinkBeginActionData) => {
    const apiInfo = makeModuleApiInfoSelector()(state, args);

    return api.sendResetLink(apiInfo, { email: args.email });
  }
});

export const sendResetLink =
  (ref: EntityRef, data: LoginForm.View.SendResetLinkFormData) =>
  (dispatch: Dispatch<any>, getState: () => BaseAppState) => {
    const state = getState();
    const localState = localStateSelector(state, ref);

    if (localState.status !== Status.Saving) {
      dispatch(_sendResetLink({ ...ref, ...data }));
    }
  };

export type ResendLinkBeginActionData = EntityRef & {};
export type ResendLinkSuccessActionData = LoginForm.Api.ResendLinkResponse;
const _resendLink = apiAction<
  BaseAppState,
  ResendLinkBeginActionData,
  LoginForm.Api.ResendLinkResponse,
  ResendLinkSuccessActionData
>({
  types: {
    begin: Actions.ResendLinkBegin,
    failure: Actions.ResendLinkFailure,
    success: [({}) => replaceUrlQuery(omit(parseQueryString(), ["t", "s"])), Actions.ResendLinkSuccess]
  },
  action: (state: BaseAppState, dispatch: any, args: ResendLinkBeginActionData) => {
    const apiInfo = makeModuleApiInfoSelector()(state, args);

    const qs = parseQueryString(window.location.href) || {};

    return api.resendLink(apiInfo, { t: qs.t, s: qs.s });
  }
});

export const resendLink = (ref: EntityRef) => (dispatch: Dispatch<any>, getState: () => BaseAppState) => {
  const state = getState();
  const localState = localStateSelector(state, ref);

  if (localState.status !== Status.Saving) {
    dispatch(_resendLink(ref));
  }
};

export type SetFieldErrorsActionData = EntityRef & {
  fieldErrors: GenericMap<string | null>;
};
export const setFieldErrors = (ref: EntityRef, fieldErrors: GenericMap<string | null>) => ({
  type: Actions.SetFieldErrors,
  data: {
    ...ref,
    fieldErrors
  }
});

export type SetModeActionData = EntityRef & {
  mode: LoginForm.Mode;
};
export const setMode = (ref: EntityRef, mode: LoginForm.Mode) => ({
  type: Actions.SetMode,
  data: {
    ...ref,
    mode
  }
});

export const goToMode = (ref: EntityRef) => (dispatch: Dispatch<any>, getState: () => BaseAppState) => {
  const state = getState();
  const localState = localStateSelector(state, ref);

  dispatch(setMode(ref, localState.mode === "login" ? "resetPassword" : "login"));
};
